import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { ChatSession } from '@src/domain/chat/entities/chat-session.entity';
import { ChatMessage } from '@src/domain/chat/value_objects/chat-message.value-object';
import { ChatConnectionStatus } from '@src/domain/chat/value_objects/connection-status.value-object';
import { WebSocketChatRepository } from '@src/infrastructure/chat/websocket-chat.repository';
import { SendMessageUseCase } from '@src/application/chat/usecases/send-message.usecase';
import { ConnectToChatUseCase } from '@src/application/chat/usecases/connect-to-chat.usecase';
import { DisconnectFromChatUseCase } from '@src/application/chat/usecases/disconnect-from-chat.usecase';
import { useAuth } from './AuthContext';
import { useQueryClient } from '@tanstack/react-query';

interface ChatContextType {
    session: ChatSession | null;
    messages: ChatMessage[];
    connectionStatus: ChatConnectionStatus;
    isConnected: boolean;
    isSending: boolean;
    chatPascoIds: string[];
    selectedMessageId: string | null;
    sendMessage: (query: string) => Promise<void>;
    connect: () => Promise<void>;
    disconnect: () => void;
    clearMessages: () => void;
    selectMessage: (messageId: string | null) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
    children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
    const { user, isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const [session, setSession] = useState<ChatSession | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [connectionStatus, setConnectionStatus] = useState<ChatConnectionStatus>(
        ChatConnectionStatus.disconnected()
    );
    const [isSending, setIsSending] = useState(false);
    const [chatPascoIds, setChatPascoIds] = useState<string[]>([]);
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

    // Initialize repository and use cases
    const [repository] = useState(() => {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
        return new WebSocketChatRepository(baseUrl);
    });

    const [sendMessageUseCase] = useState(() => new SendMessageUseCase(repository));
    const [connectUseCase] = useState(() => new ConnectToChatUseCase(repository));
    const [disconnectUseCase] = useState(() => new DisconnectFromChatUseCase(repository));

    // Initialize session when user authenticates
    useEffect(() => {
        if (isAuthenticated && user) {
            const newSession = ChatSession.create({
                sessionId: `session_${user.userId}_${Date.now()}`,
                userId: user.userId,
            });
            setSession(newSession);
        } else {
            setSession(null);
            setMessages([]);
            setChatPascoIds([]);
        }
    }, [isAuthenticated, user]);

    // Subscribe to repository events
    useEffect(() => {
        const unsubscribeMessage = repository.onMessageReceived((message) => {
            setMessages((prev) => [...prev, message]);
            setChatPascoIds((prev) => [...new Set([...prev, ...message.pascoIds])]);
            setIsSending(false);

            // Auto-select message if it has documents
            if (message.pascoIds.length > 0) {
                setSelectedMessageId(message.id);
            }

            // Update session
            if (session) {
                session.addMessage(message);
            }

            // Refresh credit balance after receiving assistant's response (chat consumes credits)
            // Only refetch for assistant messages (not user messages)
            if (message.role === 'assistant') {
                if (import.meta.env.DEV) {
                    console.log('🔄 Assistant response received, refetching credit balance...');
                }

                queryClient.refetchQueries({
                    queryKey: ['credits', 'balance'],
                    type: 'active'
                }).then(() => {
                    if (import.meta.env.DEV) {
                        console.log('✅ Credit balance refetch completed');
                    }
                });
            }
        });

        const unsubscribeStatus = repository.onConnectionStatusChange((status) => {
            setConnectionStatus(status);

            // Update session
            if (session) {
                session.updateConnectionStatus(status);
            }
        });

        const unsubscribeError = repository.onError((error) => {
            console.error('Chat error:', error);

            // Add error message to chat
            const errorMessage = ChatMessage.create({
                id: `error_${Date.now()}`,
                role: 'assistant',
                content: `Error: ${error.message}`,
                timestamp: new Date(),
            });

            setMessages((prev) => [...prev, errorMessage]);
            setIsSending(false);
        });

        return () => {
            unsubscribeMessage();
            unsubscribeStatus();
            unsubscribeError();
        };
    }, [repository, session, queryClient]);

    const connect = useCallback(async () => {
        if (!isAuthenticated) {
            throw new Error('User must be authenticated to connect to chat');
        }
        await connectUseCase.execute();
    }, [connectUseCase, isAuthenticated]);

    const disconnect = useCallback(() => {
        disconnectUseCase.execute();
    }, [disconnectUseCase]);

    const sendMessage = useCallback(
        async (query: string) => {
            if (!isAuthenticated || !user) {
                throw new Error('User must be authenticated to send messages');
            }

            // Add user message immediately
            const userMessage = ChatMessage.create({
                id: `user_${Date.now()}`,
                role: 'user',
                content: query,
                timestamp: new Date(),
            });

            setMessages((prev) => [...prev, userMessage]);
            setIsSending(true);

            // Update session
            if (session) {
                session.addMessage(userMessage);
            }

            try {
                await sendMessageUseCase.execute({
                    query,
                    phoneNumber: user.phone,
                    email: user.email,
                });

                // Refresh credit balance after sending message (consumes credits)
                if (import.meta.env.DEV) {
                    console.log('🔄 Message sent successfully, refetching credit balance...');
                }

                // Force refetch the credit balance
                await queryClient.refetchQueries({
                    queryKey: ['credits', 'balance'],
                    type: 'active'
                });

                if (import.meta.env.DEV) {
                    console.log('✅ Credit balance refetch completed');
                }
            } catch (error) {
                setIsSending(false);
                throw error;
            }
        },
        [sendMessageUseCase, isAuthenticated, user, session, queryClient]
    );

    const clearMessages = useCallback(() => {
        setMessages([]);
        setChatPascoIds([]);
        setSelectedMessageId(null);
        if (session) {
            session.clearMessages();
        }
    }, [session]);

    const selectMessage = useCallback((messageId: string | null) => {
        setSelectedMessageId(messageId);
    }, []);

    const value: ChatContextType = {
        session,
        messages,
        connectionStatus,
        isConnected: connectionStatus.isConnected,
        isSending,
        chatPascoIds,
        selectedMessageId,
        sendMessage,
        connect,
        disconnect,
        clearMessages,
        selectMessage,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextType {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}
